import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Menu, X, MapPin, Phone, Globe, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import AuthModal from "./auth/AuthModal";
import CourseSection from "./landing/CourseSection";
import { useAuth } from "./auth/AuthProvider";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [activeAuthTab, setActiveAuthTab] = useState<"login" | "signup">(
    "login",
  );
  const { user } = useAuth();
  const [branches, setBranches] = useState<any[]>([]);
  const [about, setAbout] = useState<any | null>(null);

  useEffect(() => {
    const fetchBranches = async () => {
      const snap = await getDocs(collection(db, "branches"));
      setBranches(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    const fetchAbout = async () => {
      const aboutSnap = await getDocs(collection(db, "content"));
      const aboutDoc = aboutSnap.docs.find((doc) => doc.id === "about");
      if (aboutDoc) setAbout(aboutDoc.data());
    };
    fetchBranches();
    fetchAbout();
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const openLoginModal = () => {
    setActiveAuthTab("login");
    setIsAuthModalOpen(true);
  };

  const openSignupModal = () => {
    setActiveAuthTab("signup");
    setIsAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-mss-red flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-mss-orange flex items-center justify-center -ml-2">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-mss-blue flex items-center justify-center -ml-2">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="font-bold text-xl ml-2">MSS Driving School</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <a
              href="#about"
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              About
            </a>
            <a
              href="#courses"
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              Courses
            </a>
            <a
              href="#branches"
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              Branches
            </a>
            <div className="flex items-center space-x-2">
              {user ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {user.displayName}
                  </span>
                  <Link to="/profile">
                    <Button variant="outline">Profile</Button>
                  </Link>
                </div>
              ) : (
                <>
                  <Button variant="outline" onClick={openLoginModal}>
                    Log in
                  </Button>
                  <Button onClick={openSignupModal}>Sign up</Button>
                </>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t">
            <div className="container py-4 space-y-3">
              <a
                href="#about"
                className="block py-2 text-sm font-medium hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </a>
              <a
                href="#courses"
                className="block py-2 text-sm font-medium hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Courses
              </a>
              <a
                href="#branches"
                className="block py-2 text-sm font-medium hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Branches
              </a>
              <div className="pt-2 flex flex-col space-y-2">
                {user ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      {user.displayName}
                    </span>
                    <Link to="/profile">
                      <Button variant="outline" asChild>
                        <a>Profile</a>
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        openLoginModal();
                        setIsMenuOpen(false);
                      }}
                    >
                      Log in
                    </Button>
                    <Button
                      onClick={() => {
                        openSignupModal();
                        setIsMenuOpen(false);
                      }}
                    >
                      Sign up
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative bg-background">
          <div className="container py-16 md:py-24 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                  Start Your Driving Journey With Confidence
                </h1>
                <p className="text-lg text-muted-foreground">
                  Professional driving instruction with experienced teachers and
                  modern vehicles. Get your license with our comprehensive
                  courses.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button size="lg" onClick={openSignupModal}>
                    Enroll Now
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <a href="#courses">View Courses</a>
                  </Button>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-lg overflow-hidden"
              >
                <img
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80"
                  alt="Driving lesson"
                  className="w-full h-full object-cover rounded-lg"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-16 bg-muted/50">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl font-bold tracking-tight">
                {about?.title || "About MSS Driving School"}
              </h2>
              <p className="text-muted-foreground">
                {about?.description ||
                  "With over 15 years of experience, MSS Driving School has been the leading driving school in the region. Our mission is to create safe, confident, and responsible drivers through comprehensive training and personalized instruction."}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {(about?.features || [
                {
                  title: "Experienced Instructors",
                  description:
                    "Our instructors have years of professional driving experience and are certified to teach defensive driving techniques.",
                },
                {
                  title: "Modern Fleet",
                  description:
                    "Learn on well-maintained, modern vehicles equipped with dual controls for safety during your training sessions.",
                },
                {
                  title: "Flexible Scheduling",
                  description:
                    "We offer flexible class schedules to accommodate your busy lifestyle, with weekend and evening options available.",
                },
              ]).map((feature, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <div className="text-center space-y-3">
                      <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-bold text-xl">
                          {idx + 1}
                        </span>
                      </div>
                      <h3 className="font-semibold text-xl">{feature.title}</h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Statistics Section (Dynamic, No Card) */}
        {about?.stats && (
          <section className="py-16 bg-background">
            <div className="container">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div className="space-y-2">
                  <p className="text-4xl font-bold text-primary">
                    {about.stats.graduates}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Graduates
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl font-bold text-primary">
                    {about.stats.experience}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Years Experience
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl font-bold text-primary">
                    {about.stats.vehicles}
                  </p>
                  <p className="text-sm text-muted-foreground">Vehicles</p>
                </div>
                <div className="space-y-2">
                  <p className="text-4xl font-bold text-primary">
                    {about.stats.passRate}
                  </p>
                  <p className="text-sm text-muted-foreground">Pass Rate</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Courses Section */}
        <section id="courses" className="py-16 bg-muted/50">
          <div className="container">
            <CourseSection />
          </div>
        </section>

        {/* Branches Section */}
        <section id="branches" className="py-16 bg-muted/50">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
              <h2 className="text-3xl font-bold tracking-tight">Our Branches</h2>
              <p className="text-muted-foreground">
                MSS Driving School has multiple branches to serve you better. Find a branch near you!
              </p>
            </div>
            {branches.length === 0 ? (
              <div className="text-center text-muted-foreground">No branches available at the moment.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...branches].sort((a, b) => a.name.localeCompare(b.name)).map((branch) => (
                  <Card key={branch.id}>
                    <CardContent className="pt-6 pb-6">
                      <div className="space-y-3">
                        <h3 className="font-semibold text-xl">{branch.name}</h3>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{branch.address}</span>
                        </div>
                        {branch.contact && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{branch.contact}</span>
                          </div>
                        )}
                        {branch.email && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{branch.email}</span>
                          </div>
                        )}
                        {branch.social && (
                          <div className="flex items-center gap-2">
                            <a
                              href={branch.social}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:underline"
                            >
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              Visit Social Media
                            </a>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container flex flex-col items-center justify-center text-center">
          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-mss-red flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-mss-orange flex items-center justify-center -ml-2">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-mss-blue flex items-center justify-center -ml-2">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="font-bold text-xl ml-2">
                MSS Driving School
              </span>
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Professional driving instruction since 2008. Creating safe and
              confident drivers with MSS excellence.
            </p>
          </div>
          <Separator className="my-8 w-full max-w-lg mx-auto" />
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} MSS Driving School. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultTab={activeAuthTab}
      />
    </div>
  );
};

export default Home;
